"use client";

import { useSyncExternalStore } from "react";

import { safeLocalSet } from "@/lib/browser-storage";
import { THEME_CHANGE_EVENT, THEME_STORAGE_KEY, type Theme } from "@/lib/theme-contract";

interface ThemeToggleProps {
  fullWidth?: boolean;
}

function currentTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

function setDocumentTheme(theme: Theme): void {
  if (theme === "dark") {
    document.documentElement.dataset.theme = "dark";
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

function subscribeToTheme(onChange: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
    setDocumentTheme(event.newValue === "dark" ? "dark" : "light");
    onChange();
  };

  window.addEventListener(THEME_CHANGE_EVENT, onChange);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", handleStorage);
  };
}

export default function ThemeToggle({ fullWidth = false }: ThemeToggleProps) {
  const theme = useSyncExternalStore(subscribeToTheme, currentTheme, () => "light");
  const isDark = theme === "dark";
  const targetTheme: Theme = isDark ? "light" : "dark";
  const label = `Switch to ${targetTheme} theme`;

  const toggleTheme = () => {
    setDocumentTheme(targetTheme);
    safeLocalSet(THEME_STORAGE_KEY, targetTheme);
    window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
      className={
        fullWidth
          ? "flex min-h-11 w-full items-center justify-between rounded-lg border border-border-default px-3 py-2 text-sm font-semibold text-text-primary transition hover:bg-surface-brand-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          : "inline-flex size-11 items-center justify-center rounded-md border border-border-default text-text-primary transition hover:bg-surface-brand-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      }
    >
      {fullWidth ? <span>{label}</span> : null}
      {isDark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

function MoonIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="4" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41"
        strokeLinecap="round"
      />
    </svg>
  );
}
